<template>
  <lf-modal :close="close" :title="title">
    <template #content>
      <form novalidate @submit="submit">
        <div class="h-70-screen space-y-6 overflow-y-auto p-6">
          <div
            v-if="
              isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.legal_name) ||
              isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.dba)
            "
            class="flex flex-row flex-wrap space-x-0 sm:space-x-5"
          >
            <lf-input
              v-if="
                isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.legal_name)
              "
              :placeholder="$t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.LEGAL_NAME')"
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.legal_name"
              no-margin
            />
            <lf-input
              v-if="isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.dba)"
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.dba"
              :placeholder="$t('DEALS.DEAL_PROGRESS.PERSONAL_INFO.DBA')"
              no-margin
            />
          </div>
          <div
            v-if="
              isFieldVisible(
                AFFILITED_BUSINESS_INFO_FIELD_IDS.stated_monthly_revenue
              ) || isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.entity_type)
            "
            class="flex flex-row flex-wrap space-x-0 sm:space-x-5"
          >
            <lf-currency
              v-if="
                isFieldVisible(
                  AFFILITED_BUSINESS_INFO_FIELD_IDS.stated_monthly_revenue
                )
              "
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.stated_monthly_revenue"
              :placeholder="$t('DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE')"
              :model-value="
                affiliatedBusinessInfo?.[
                  AFFILITED_BUSINESS_INFO_FIELD_IDS.stated_monthly_revenue
                ]
              "
              no-margin
            />
            <lf-dropdown
              v-if="
                isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.entity_type)
              "
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.entity_type"
              :placeholder="
                $t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.STRUCTURE_TYPE')
              "
              :options="businessEntityTypes"
              sort
            />
          </div>
          <div
            v-if="
              isFieldVisible(
                AFFILITED_BUSINESS_INFO_FIELD_IDS.state_of_incorporation
              ) || isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.start_date)
            "
            class="flex flex-row flex-wrap space-x-0 sm:space-x-5"
          >
            <lf-input
              v-if="
                isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.start_date)
              "
              :placeholder="$t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.START_DATE')"
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.start_date"
              type="date"
              no-margin
            />
            <lf-dropdown
              v-if="
                isFieldVisible(
                  AFFILITED_BUSINESS_INFO_FIELD_IDS.state_of_incorporation
                )
              "
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.state_of_incorporation"
              :placeholder="
                $t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.STATE_OF_INCORPORATION')
              "
              :options="states"
              full-width
              sort
            />
          </div>
          <div
            v-if="
              isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.ein) ||
              isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.phone_numbers)
            "
            class="flex flex-row flex-wrap space-x-0 sm:space-x-5"
          >
            <lf-input
              v-if="isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.ein)"
              :placeholder="
                isActiveDealCanadian
                  ? $t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.BN')
                  : $t('DEALS.DEAL_PROGRESS.BUSINESS_INFO.EIN')
              "
              :name="AFFILITED_BUSINESS_INFO_FIELD_IDS.ein"
              no-margin
            />
            <multiple-inputs-with-primary-value
              v-if="
                isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.phone_numbers)
              "
              name="phone_numbers"
              is-wfb-object
              :all-values="
                affiliatedBusinessInfo?.affiliated_businesses_phone_numbers ||
                []
              "
              :placeholder="$t('DEALS.DEAL_PROGRESS.PERSONAL_INFO.PHONE')"
              :error-messages="telephoneErrors"
              :add-more-text="$t('COMMON.ADD_PHONE')"
              @primary-changed="changePrimaryPhone"
              @remove-phone="removePhone"
            />
          </div>
          <div v-if="isFieldVisible(AFFILITED_BUSINESS_INFO_FIELD_IDS.address)">
            <lf-h3 class="my-4 pb-3">
              {{ $t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS") }}
            </lf-h3>
            <multiple-addresses v-model:addresses="allAddresses" />
          </div>
        </div>
        <div class="flex justify-end border-t border-gray-200 p-6">
          <outline-button class="mr-3" @click="close">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <primary-button
            type="submit"
            :disabled="isSubmitting"
            role="saveBusiness"
          >
            {{ $t("COMMON.SAVE") }}
          </primary-button>
        </div>
      </form>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { format } from "date-fns";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import { useForm } from "vee-validate";

import {
  AFFILITED_BUSINESS_INFO_FIELD_IDS,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import { filterOutEmptyAddresses } from "@/helpers/deals";
import { looksLikeCurrency } from "@/helpers/formatting";
import { dispatchAction } from "@/helpers/vee-validate";
import { useDeals } from "@/hooks/deals";
import { useWorkflowFields } from "@/hooks/workflow";

import LfModal from "@/components/ui/Modal.vue";
import LfCurrency from "@/components/ui/inputs/LfCurrency.vue";
import MultipleAddresses from "@/views/deals/components/MultipleAddresses.vue";
import MultipleInputsWithPrimaryValue from "@/views/deals/components/MultipleInputsWithPrimaryValue.vue";

import type { IAddress } from "@/models/common";
import type {
  AffiliatedBusinessInfoFields,
  DeleteRecord,
  PhoneNumber,
} from "@/models/workflows";
import type { PropType } from "vue";

const props = defineProps({
  affiliatedBusinessInfo: {
    type: Object as PropType<AffiliatedBusinessInfoFields | null>,
    default: () => ({
      affiliated_businesses_id: null,
      affiliated_businesses_addresses: [],
      affiliated_businesses_dba: "",
      affiliated_businesses_ein: "",
      affiliated_businesses_entity_type: "",
      affiliated_businesses_legal_name: "",
      affiliated_businesses_phone_numbers: [],
      affiliated_businesses_start_date: "",
      affiliated_businesses_state_of_incorporation: "",
    }),
  },
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const emit = defineEmits<{
  "update-data": [];
}>();

const { t } = useI18n();
const { isFieldVisible } = useWorkflowFields(
  WORKFLOW_BLOCKS_IDS.affiliated_businesses
);
const store = useStore();
const { isActiveDealCanadian } = useDeals();

const telephoneErrors = ref<string[]>([]);
const telephoneToDelete = ref<DeleteRecord[]>([]);
const primaryPhoneId = ref();
const allAddresses = ref<IAddress[]>(
  (props.affiliatedBusinessInfo
    ?.affiliated_businesses_addresses as IAddress[]) || []
);

const changePrimaryPhone = (id: number) => {
  primaryPhoneId.value = id;
};

const getFormattedPhones = (values: Record<string, string | unknown>) => {
  const telephones: PhoneNumber[] = [];
  Object.keys(values).forEach((key) => {
    const telephone = {} as PhoneNumber;
    if (key.includes("phone_numbers_") && !isEmpty(values[key])) {
      const id = key.replace("phone_numbers_", "");
      const exsist =
        props.affiliatedBusinessInfo?.affiliated_businesses_phone_numbers?.find(
          (phone) => phone.id === Number(id)
        );
      if (exsist) {
        telephone.id = Number(id);
      }
      telephone.phone_number = String(values[key]);
      telephone.is_primary = false;
      if (id == primaryPhoneId.value) {
        telephone.is_primary = true;
      }
      delete values[key];
      telephones.push(telephone);
    }
  });
  values.affiliated_businesses_phone_numbers = telephones;
};

const states = computed<Record<string, string>>(
  () => store.getters["options/states"]
);

const businessEntityTypes = computed<Record<string, string>>(() =>
  store.getters["options/businessEntityTypes"]()
);

const title = computed(
  () =>
    (props.affiliatedBusinessInfo?.affiliated_business_id
      ? t("COMMON.EDIT")
      : t("COMMON.ADD")) +
    " " +
    t("DEALS.DEAL_PROGRESS.AFFILIATED_BUSINESSES_INFO.TITLE", 1)
);

const removePhone = (id: number) => {
  const phone = last(
    props.affiliatedBusinessInfo?.affiliated_businesses_phone_numbers?.filter(
      (phone) => phone.id?.toString() === id.toString()
    )
  );
  telephoneToDelete.value.push({
    id: phone?.id || "",
    _delete: true,
  });
};

const { handleSubmit, isSubmitting } = useForm({
  initialValues: {
    ...props.affiliatedBusinessInfo,
    affiliated_businesses_start_date: props.affiliatedBusinessInfo
      ?.affiliated_businesses_start_date
      ? format(
          new Date(
            props.affiliatedBusinessInfo
              ?.affiliated_businesses_start_date as string
          ),
          "yyyy-MM-dd"
        )
      : "",
  },
});

const submit = handleSubmit(async (values, actions) => {
  telephoneErrors.value = [];
  if (props.affiliatedBusinessInfo?.affiliated_business_id) {
    values.affiliated_business_id =
      props.affiliatedBusinessInfo.affiliated_business_id;
  }

  getFormattedPhones(values);
  values.affiliated_businesses_addresses = filterOutEmptyAddresses(
    allAddresses.value
  );

  values.affiliated_businesses_state_of_incorporation =
    states.value[values.affiliated_businesses_state_of_incorporation as string];
  values.affiliated_business_id =
    props.affiliatedBusinessInfo?.affiliated_business_id;

  if (
    looksLikeCurrency(
      String(values.affiliated_businesses_stated_monthly_revenue)
    )
  ) {
    values.affiliated_businesses_stated_monthly_revenue = Number(
      String(values.affiliated_businesses_stated_monthly_revenue).replace(
        /[^\d.]/g,
        ""
      )
    );
  }

  values.affiliated_businesses_phone_numbers = [
    ...(values.affiliated_businesses_phone_numbers
      ? values.affiliated_businesses_phone_numbers
      : []),
    ...telephoneToDelete.value,
  ] as PhoneNumber[];

  try {
    await dispatchAction(
      { [WORKFLOW_BLOCKS_IDS.affiliated_businesses]: [values] },
      actions,
      "workflows/updateWorkflowSnapshotFields",
      store
    );
  } catch (error) {
    const { formattedErrors } = error as {
      formattedErrors?: Record<string, string>;
    };
    if (!formattedErrors) {
      return;
    }
    Object.keys(formattedErrors).forEach((key) => {
      if (key.includes("phone_number")) {
        telephoneErrors.value.push(formattedErrors[key]);
      }
    });
    return;
  }

  props.close();
  emit("update-data");
});
</script>
